/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import { cx } from "./cx";
import { Icon } from "./icons";
import { ToastContext, type ToastApi, type ToastOptions, type ToastTone } from "./toast-context";

const TOAST_ICON: Record<Exclude<ToastTone, "progress">, LucideIcon> = {
  success: CircleCheck,
  neutral: Info,
  error: CircleAlert,
};

/**
 * The answer to an action (the canvas's Action feedback board). Inline by
 * default; `floating` docks it at the bottom centre over the page.
 */
export function Toast({
  tone = "success",
  icon,
  actionLabel,
  onAction,
  onClose,
  closeLabel,
  onDone,
  duration,
  floating,
  compact,
  children,
}: {
  tone?: ToastTone;
  icon?: LucideIcon | false;
  actionLabel?: string;
  onAction?: () => void;
  /** Shows a dismiss ×. */
  onClose?: () => void;
  closeLabel?: string;
  /** Called when it times out (not for progress). */
  onDone?: () => void;
  /** ms; 0 keeps it. Default 2000, or 5000 with an action. */
  duration?: number;
  floating?: boolean;
  compact?: boolean;
  children?: ReactNode;
}) {
  useEffect(() => {
    if (!onDone || duration === 0 || tone === "progress") return;
    const timer = window.setTimeout(onDone, duration ?? (actionLabel ? 5000 : 2000));
    return () => window.clearTimeout(timer);
  }, [children, onDone, duration, tone, actionLabel]);

  if (children == null || children === "") return null;
  const lead =
    tone === "progress" ? (
      <span className="rd-spin" aria-hidden="true" />
    ) : icon === false ? null : (
      <Icon icon={icon ?? TOAST_ICON[tone]} size={15} />
    );
  const toast = (
    <div
      className={cx("rd-toast", `rd-toast-${tone}`, compact && "rd-toast-compact")}
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
    >
      {lead}
      <span className="rd-toast-text">{children}</span>
      {actionLabel ? (
        <button type="button" className="rd-toast-action" onClick={onAction}>
          {actionLabel}
        </button>
      ) : null}
      {onClose ? (
        <button type="button" className="rd-toast-x" aria-label={closeLabel} onClick={onClose}>
          <Icon icon={X} size={14} />
        </button>
      ) : null}
    </div>
  );
  return floating ? <div className="rd-root rd-toast-dock">{toast}</div> : toast;
}

const BANNER_ICON = { info: Info, success: CircleCheck, warning: TriangleAlert, error: CircleAlert } as const;

/**
 * An inline message in the page, card, drawer or modal it is about: what
 * happened, why, and what to do. A failed action is an error Banner where the
 * action was — never a toast — and keeps the form as it was.
 */
export function Banner({
  tone = "info",
  title,
  icon,
  actions,
  onDismiss,
  dismissLabel,
  className,
  children,
}: {
  tone?: keyof typeof BANNER_ICON;
  title?: ReactNode;
  icon?: LucideIcon;
  /** "Try again", a link to fix it. */
  actions?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cx("rd-banner", `rd-banner-${tone}`, className)} role={tone === "error" || tone === "warning" ? "alert" : "status"}>
      <Icon icon={icon ?? BANNER_ICON[tone]} size={16} />
      <div className="rd-banner-body">
        {title ? <span className="rd-banner-title">{title}</span> : null}
        {children ? <span className="rd-banner-text">{children}</span> : null}
        {actions ? <div className="rd-banner-actions">{actions}</div> : null}
      </div>
      {onDismiss ? (
        <button type="button" className="rd-banner-x" aria-label={dismissLabel} onClick={onDismiss}>
          <Icon icon={X} size={14} />
        </button>
      ) : null}
    </div>
  );
}

/**
 * One floating toast at a time for the whole shell. A new one replaces the
 * old; `update(id, …)` turns a progress toast into its result.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<(ToastOptions & { id: number }) | null>(null);
  const nextId = useRef(1);

  const show = useCallback((options: ToastOptions) => {
    const id = nextId.current++;
    setCurrent({ ...options, id });
    return id;
  }, []);
  const update = useCallback((id: number, options: ToastOptions) => {
    setCurrent((existing) => (existing && existing.id === id ? { ...options, id } : existing));
  }, []);
  const dismiss = useCallback((id?: number) => {
    setCurrent((existing) => (!existing || id === undefined || existing.id === id ? null : existing));
  }, []);
  const api = useMemo<ToastApi>(() => ({ show, update, dismiss }), [show, update, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {current ? (
        <Toast
          key={current.id}
          floating
          tone={current.tone}
          icon={current.icon}
          actionLabel={current.actionLabel}
          onAction={() => {
            current.onAction?.();
            dismiss(current.id);
          }}
          duration={current.duration}
          onDone={() => dismiss(current.id)}
        >
          {current.message}
        </Toast>
      ) : null}
    </ToastContext.Provider>
  );
}
